import { Fragment, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  ArchiveBoxIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  Squares2X2Icon,
  UserIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import AuthService from "../services/auth.service";
import LogoComponent from "./logo";

const NAVIGATION = [
  { name: "Home", href: "/" },
  { name: "How It Works", href: "/how-it-works" },
  { name: "Track Parcel", href: "/track-parcel" },
  { name: "FAQ", href: "/faq" },
  { name: "About", href: "/about-us" },
  { name: "Contact", href: "/contact" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function accountLinksFor(user) {
  const hasBusiness = user?.businesses?.length > 0;
  return [
    { name: "Your Profile", href: "/profile", icon: UserIcon },
    { name: "My Parcels", href: "/parcels", icon: ArchiveBoxIcon },
    user?.parcel_driver && { name: "Dashboard", href: "/dashboard", icon: Squares2X2Icon },
    {
      name: hasBusiness ? "Business" : "Register Business",
      href: hasBusiness ? "/business/dashboard" : "/not-a-business",
      icon: BuildingOffice2Icon,
    },
  ].filter(Boolean);
}

function Avatar({ user, size = "h-9 w-9" }) {
  if (user?.profile_pic_url) {
    return <img className={`${size} rounded-full object-cover ring-2 ring-purple-100`} src={user.profile_pic_url} alt="" />;
  }
  return (
    <span className={`${size} flex items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-500 text-sm font-medium text-white`} aria-hidden="true">
      {(user?.first_name?.[0] ?? "?").toUpperCase()}
    </span>
  );
}

export const Header = () => {
  const { pathname } = useLocation();
  const [currentUser, setCurrentUser] = useState(AuthService.getCurrentUser());
  const [scrolled, setScrolled] = useState(false);
  const user = currentUser?.user;

  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail.type === "login") {
        setCurrentUser(AuthService.getCurrentUser());
      } else if (event.detail.type === "logout") {
        setCurrentUser(null);
      }
    };

    window.addEventListener("auth-change", handleAuthChange);
    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isCurrentPage = (href) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  const logOut = () => {
    AuthService.logout();
    window.location.replace("/");
  };

  const accountLinks = accountLinksFor(user);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  return (
    <Disclosure
      as="header"
      className={classNames(
        "sticky top-0 z-50 bg-white transition-shadow duration-300",
        scrolled && "shadow-md shadow-purple-900/5",
      )}
    >
      {({ open, close }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 md:h-[72px] items-center gap-4">
              <Link to="/" className="flex-shrink-0" aria-label="Parcelin home" onClick={() => close()}>
                <LogoComponent placement="header" />
              </Link>

              <nav aria-label="Main" className="hidden lg:flex flex-1 justify-start ml-6">
                <ul className="flex items-center gap-1">
                  {NAVIGATION.map((item) => {
                    const current = isCurrentPage(item.href);
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          aria-current={current ? "page" : undefined}
                          className={classNames(
                            "px-3.5 py-2 rounded-full text-sm font-medium transition-colors",
                            current ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:text-purple-700 hover:bg-gray-50",
                          )}
                        >
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="ml-auto lg:ml-0 flex items-center gap-2">
                {!currentUser && (
                  <div className="hidden sm:flex items-center gap-2">
                    <Link to="/login" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:text-purple-700 hover:bg-gray-50 transition-colors">
                      Log In
                    </Link>
                    <Link to="/signup" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 shadow-sm shadow-purple-600/30 transition-colors">
                      Sign Up
                    </Link>
                  </div>
                )}

                {currentUser && (
                  <Menu as="div" className="relative hidden sm:block">
                    <Menu.Button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400">
                      <span className="sr-only">Open user menu</span>
                      <Avatar user={user} />
                      <span className="hidden md:block text-sm font-medium text-gray-800 max-w-[8rem] truncate">{user?.first_name}</span>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-150"
                      enterFrom="opacity-0 translate-y-1 scale-95"
                      enterTo="opacity-100 translate-y-0 scale-100"
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-60 origin-top-right rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-gray-100 focus:outline-none">
                        <div className="px-3 py-2.5 mb-1 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900 truncate">{fullName || "Your account"}</p>
                          {user?.cellphone && <p className="text-xs text-gray-500 truncate">{user.cellphone}</p>}
                        </div>
                        {accountLinks.map((link) => (
                          <Menu.Item key={link.href}>
                            {({ active }) => (
                              <Link
                                to={link.href}
                                className={classNames(
                                  active ? "bg-purple-50 text-purple-700" : "text-gray-700",
                                  "flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                                )}
                              >
                                <link.icon className="h-4 w-4" aria-hidden="true" />
                                {link.name}
                              </Link>
                            )}
                          </Menu.Item>
                        ))}
                        <div className="my-1 border-t border-gray-100" />
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={logOut}
                              className={classNames(
                                active ? "bg-red-50" : "",
                                "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-red-600 transition-colors",
                              )}
                            >
                              <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                              Sign Out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}

                <Disclosure.Button className="lg:hidden inline-flex items-center justify-center rounded-full h-10 w-10 text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400">
                  <span className="sr-only">{open ? "Close main menu" : "Open main menu"}</span>
                  {open ? <XMarkIcon className="h-6 w-6" aria-hidden="true" /> : <Bars3Icon className="h-6 w-6" aria-hidden="true" />}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Transition
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-2"
          >
            <Disclosure.Panel className="lg:hidden border-t border-gray-100 bg-white shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
              <nav aria-label="Mobile" className="px-4 py-3">
                <ul className="grid grid-cols-2 gap-1">
                  {NAVIGATION.map((item) => {
                    const current = isCurrentPage(item.href);
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => close()}
                          aria-current={current ? "page" : undefined}
                          className={classNames(
                            "block rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                            current ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="border-t border-gray-100 px-4 py-4">
                {currentUser ? (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar user={user} size="h-10 w-10" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{fullName || "Your account"}</p>
                        {user?.cellphone && <p className="text-xs text-gray-500">{user.cellphone}</p>}
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {accountLinks.map((link) => (
                        <li key={link.href}>
                          <Link to={link.href} onClick={() => close()} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            <link.icon className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            {link.name}
                          </Link>
                        </li>
                      ))}
                      <li>
                        <button type="button" onClick={logOut} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50">
                          <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/login" onClick={() => close()} className="flex items-center justify-center gap-1.5 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                      <ArrowLeftOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                      Log In
                    </Link>
                    <Link to="/signup" onClick={() => close()} className="flex items-center justify-center gap-1.5 rounded-full bg-purple-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-700">
                      <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};
